import { ArcherElement } from "react-archer";
import { useState, useEffect } from "react";
import RuleEngineBackend from "../../services/RuleEngineBackend";
import PPUtils from "../../../utils/PPUtils";

function resolveProviderFilterId(providerFilter) {
     if (!providerFilter) return -1;
     const filterId = providerFilter.filter_id ?? providerFilter.providerFilterId ?? -1;
     const numericId = Number(filterId);
     return Number.isFinite(numericId) ? numericId : -1;
}

export default function ForeignShopRuleCard({obj, index, onEditRule, onDeleteRule}){ 

     switch(obj.keyString) {
          case 'priceTracking': return <PriceTrackingTile ruledata={obj} index={index} onEditRule={onEditRule} onDeleteRule={onDeleteRule}/>
          case 'foreignShopstock': return <ForeignShopStockTile ruledata={obj} index={index} onEditRule={onEditRule} onDeleteRule={onDeleteRule}/>
          case 'useAllPricesFromGroup': return <UseAllPricesFromGroupTile ruledata={obj} index={index} onEditRule={onEditRule} onDeleteRule={onDeleteRule}/>
          default: return ""
     }
}

function PriceTrackingTile({ruledata, index, onEditRule, onDeleteRule}){
     const [shops, setShops] = useState([]);
     const [providerFilters, setProviderFilters] = useState([]);

     useEffect(() => {
          const fetchShops = async () => {
               const auth = PPUtils.getLogin();
               const shopData = await RuleEngineBackend.getShops(auth.token);
               if (shopData) {
                    setShops(shopData);
               }
          };
          fetchShops();
     }, []);

     useEffect(() => {
          const fetchProviderFilters = async () => {
               const request = PPUtils.getCustomerRequestContext();
               if (request == null) {
                    return
               }
               const filters = await RuleEngineBackend.getProviderFilters(request.token, request.customerId);
               if (Array.isArray(filters)) {
                    setProviderFilters(filters);
               }
          };
          fetchProviderFilters();
     }, []);

     const providerFilterId = resolveProviderFilterId(ruledata.providerFilter);
     const selectedProviderFilter = providerFilterId !== -1
          ? providerFilters.find((filter) => Number(filter.id) === providerFilterId)
          : null;
     const providerFilterLabel = selectedProviderFilter?.name
          || (providerFilterId !== -1 ? `Filter #${providerFilterId}` : null);

     const shopPriorities = [];
     if (ruledata.shopKeysWithPrio && shops.length > 0) {
          Object.entries(ruledata.shopKeysWithPrio).forEach(([shopId, priority]) => {
               const shop = shops.find(s => s.id === parseInt(shopId));
               if (shop) {
                    shopPriorities.push({
                         id: parseInt(shopId),
                         priority: parseInt(priority),
                         name: shop.name
                    });
               }
          });
          shopPriorities.sort((left, right) => {
               const leftPrio = Number.isFinite(left.priority) ? left.priority : Number.MAX_SAFE_INTEGER;
               const rightPrio = Number.isFinite(right.priority) ? right.priority : Number.MAX_SAFE_INTEGER;
               if (leftPrio !== rightPrio) {
                    return leftPrio - rightPrio;
               }
               return left.name.localeCompare(right.name);
          });
     }

     return (
          <div className="card mb-2 shadow-sm position-relative" key={index}>
               <button type="button" className="card-header bg-light border-bottom py-1 px-2 pp-clickable-block" onClick={() => onEditRule?.(ruledata)}>
                    <div className="d-flex align-items-center justify-content-center">
                         <i className="fas fa-chart-line me-2 text-info"></i>
                         <h6 className="card-title mb-0 fw-bold">Preistracking Mitbewerb</h6>
                    </div>
               </button>
               <button type="button" className="card-body py-2 px-2 pp-clickable-block" onClick={() => onEditRule?.(ruledata)}>
                    <div className="row g-1">
                         {shopPriorities.length > 0 && (
                              <div className="col-12 mb-2">
                                   <div className="d-flex align-items-start flex-wrap">
                                        <small className="text-dark fw-semibold me-2">Shop-Priorität:</small>
                                        <div className="d-flex flex-wrap">
                                             {shopPriorities.map((shop) => (
                                                  <span key={shop.id} className="badge bg-light text-dark border me-1 mb-1" style={{fontSize: '0.7rem'}}>
                                                       {shop.priority}. {shop.name}
                                                  </span>
                                             ))}
                                        </div>
                                   </div>
                              </div>
                         )}
                         <div className="col-12 d-flex align-items-center">
                              <small className="text-dark fw-semibold">Anbieter:</small>
                              <span className="ms-1 badge bg-light text-dark border" style={{fontSize: '0.7rem'}}>
                                   {!ruledata.useAllProviders ? 'Nur günstigster' : 'Alle Anbieter'}
                              </span>
                         </div>
                         {providerFilterLabel && (
                              <div className="col-12 d-flex align-items-center mt-1">
                                   <small className="text-dark fw-semibold">Anbieterfilter:</small>
                                   <span className="ms-1 badge bg-light text-dark border" style={{fontSize: '0.7rem'}}>
                                        {providerFilterLabel}
                                   </span>
                              </div>
                         )}
                         {ruledata.excludeShopIfDeliveryCostAreNotAvailable && (
                              <div className="col-12 d-flex align-items-center mt-1">
                                   <small className="text-dark fw-semibold">Ausschluss:</small>
                                   <span className="ms-1 badge bg-light text-dark border" style={{fontSize: '0.7rem'}}>
                                        Versandkosten nicht verfügbar
                                   </span>
                              </div>
                         )}
                    </div>
               </button>
               {onDeleteRule && (
                    <button
                              type="button"
                              className="pp-rule-delete-btn"
                         onClick={(e) => {
                              e.stopPropagation();
                              onDeleteRule(ruledata);
                         }}
                         title="Regel löschen"
                    >
                         <i className="fas fa-trash"></i>
                    </button>
               )}
          </div>
     ); 
}

function ForeignShopStockTile({ruledata, index, onEditRule, onDeleteRule}){
     const stockItems = [];
     if (ruledata.stock[0]) stockItems.push({label: "Rot", color: "danger"});
     if (ruledata.stock[1]) stockItems.push({label: "Grün", color: "success"});
     if (ruledata.stock[2]) stockItems.push({label: "Gelb", color: "warning"});
     
     const relations = ruledata.stockRuleReference ? [{
          targetId: ruledata.stockRuleReference,
          targetAnchor: 'right',
          sourceAnchor: 'left',
          style: { strokeColor: "#2650aaff", strokeWidth: 2 }
     }] : [];
     
     return (
          <ArcherElement id={ruledata.id} relations={relations}>
               <div className="card mb-2 shadow-sm position-relative" key={index}>
                    <button type="button" className="card-header bg-light border-bottom py-1 px-2 pp-clickable-block" onClick={() => onEditRule?.(ruledata)}>
                         <div className="d-flex align-items-center justify-content-center">
                              <i className="fas fa-store me-2 text-warning"></i>
                              <h6 className="card-title mb-0 fw-bold">Fremdshop Verfügbarkeit</h6>
                         </div>
                    </button>
                    <button type="button" className="card-body py-2 px-2 pp-clickable-block" onClick={() => onEditRule?.(ruledata)}>
                         <div className="col-12">
                              <div className="d-flex align-items-start flex-wrap">
                                   <small className="text-dark fw-semibold me-2">Verfügbarkeit:</small>
                                   <div className="d-flex flex-wrap">
                                        {stockItems.length > 0 ? stockItems.map((item, i) => (
                                             <span key={i} className={`badge bg-${item.color} me-1 mb-1`} style={{fontSize: '0.7rem'}}>{item.label}</span>
                                        )) : <span className="badge bg-light text-dark border" style={{fontSize: '0.7rem'}}>Keine</span>}
                                   </div>
                              </div>
                         </div>
                    </button>
                    {onDeleteRule && (
                         <button
                              type="button"
                              className="pp-rule-delete-btn"
                              onClick={(e) => {
                                   e.stopPropagation();
                                   onDeleteRule(ruledata);
                              }}
                              title="Regel löschen"
                         >
                              <i className="fas fa-trash"></i>
                         </button>
                    )}
               </div>
          </ArcherElement>
     ); 
}

function UseAllPricesFromGroupTile({ruledata, index, onEditRule, onDeleteRule}){
     return (
          <div className="card mb-2 shadow-sm position-relative" key={index}>
               <button type="button" className="card-header bg-light border-bottom py-1 px-2 pp-clickable-block" onClick={() => onEditRule?.(ruledata)}>
                    <div className="d-flex align-items-center justify-content-center">
                         <i className="fas fa-layer-group me-2 text-primary"></i>
                         <h6 className="card-title mb-0 fw-bold">Preise von Produktgruppe einbeziehen</h6>
                    </div>
               </button>
               <button type="button" className="card-body py-2 px-2 pp-clickable-block" onClick={() => onEditRule?.(ruledata)}>
                    <div className="col-12 d-flex align-items-center">
                         <small className="text-dark fw-semibold me-1">Produktgruppe:</small>
                         <span className="badge bg-light text-dark border" style={{fontSize: '0.7rem'}}>
                              {ruledata.groupName || "Keine"}
                         </span>
                    </div>
               </button>
               {onDeleteRule && (
                    <button
                              type="button"
                              className="pp-rule-delete-btn"
                         onClick={(e) => {
                              e.stopPropagation();
                              onDeleteRule(ruledata);
                         }}
                         title="Regel löschen"
                    >
                         <i className="fas fa-trash"></i>
                    </button>
               )}
          </div>
     );
}